import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, MailCheck } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

export default function ConfirmEmailChange() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = useMemo(() => params.get("token") || "", [params]);
    const [state, setState] = useState("idle"); // idle | done | error
    const [msg, setMsg] = useState("");

    useEffect(() => {
        if (!token || token.length < 20) {
            setState("error");
            setMsg("This confirmation link is invalid.");
            return;
        }
        (async () => {
            try {
                const { data } = await api.post("/auth/email-change/confirm", { token });
                setState("done");
                setMsg(data.message || "Email updated. Please log in again with your new address.");
                setTimeout(() => navigate("/login"), 2400);
            } catch (err) {
                setState("error");
                setMsg(formatApiError(err));
            }
        })();
    }, [token, navigate]);

    return (
        <div className="min-h-[85vh] bg-background py-16">
            <div className="container-page max-w-md">
                <div className="card-soft p-8 text-center" data-testid="confirm-email-change">
                    {state === "idle" && (
                        <>
                            <Loader2 className="mx-auto mb-4 h-6 w-6 animate-spin text-emerald-600" />
                            <h1 className="font-heading font-semibold text-2xl text-foreground">Confirming your new email…</h1>
                        </>
                    )}
                    {state === "done" && (
                        <>
                            <MailCheck className="mx-auto mb-4 h-8 w-8 text-emerald-600" data-testid="confirm-email-success" />
                            <h1 className="font-heading font-semibold text-2xl text-foreground">Email updated</h1>
                            <p className="mt-2 text-sm text-muted-foreground">{msg}</p>
                            <p className="mt-3 text-xs text-muted-foreground">Redirecting you to the login page…</p>
                        </>
                    )}
                    {state === "error" && (
                        <>
                            <ShieldCheck className="mx-auto mb-4 h-8 w-8 text-rose-500" data-testid="confirm-email-error" />
                            <h1 className="font-heading font-semibold text-2xl text-foreground">Couldn&apos;t confirm</h1>
                            <p className="mt-2 text-sm text-muted-foreground">{msg}</p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
